import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Plus,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Send,
  Trash2,
  RefreshCw,
  X,
  ChevronDown,
  Receipt,
  Download,
  Mail,
  Search,
  LogOut,
  CreditCard,
  Link as LinkIcon,
  Copy
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getAdminToken, clearAdminToken } from "@/pages/AdminLoginPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Video Production",
  "Web Design",
  "AI Bot Solutions",
  "YouTube Content",
  "Instagram Content",
  "Facebook Content",
  "Audio & Jingles",
  "Video Editing",
  "OBS Consultation",
  "Photography",
  "Logo Design",
  "Scripting",
  "Commercial Ads",
  "Other"
];

const statusColors = {
  draft: "bg-gray-200 text-gray-700",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-700",
  cancelled: "bg-gray-300 text-gray-600"
};

const statusIcons = {
  draft: FileText,
  sent: Send,
  paid: CheckCircle,
  overdue: AlertCircle,
  cancelled: X
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sendingEmail, setSendingEmail] = useState(null);
  const [generatingPayLink, setGeneratingPayLink] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);

  const verifyAuth = async () => {
    const token = getAdminToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      clearAdminToken();
      navigate("/admin/login");
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      let url = `${API}/invoices?`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}`;
      
      const [invoicesRes, statsRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/invoices/stats/summary`)
      ]);
      setInvoices(invoicesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyAuth();
    fetchInvoices();
  }, [filterStatus]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInvoices();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const markAsPaid = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/mark-paid`);
      toast.success("Invoice marked as paid");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const updateStatus = async (invoiceId, status) => {
    try {
      await axios.patch(`${API}/invoices/${invoiceId}`, { status });
      toast.success("Status updated");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const downloadPdf = (invoiceId) => {
    window.open(`${API}/invoices/${invoiceId}/pdf`, '_blank');
  };

  const sendEmail = async (invoiceId) => {
    setSendingEmail(invoiceId);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send-email`);
      toast.success("Invoice sent to client!");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(null);
    }
  };

  const checkOverdue = async () => {
    try {
      const res = await axios.post(`${API}/invoices/check-overdue`);
      toast.success(res.data.message);
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to check overdue invoices");
    }
  };

  const generatePaymentLink = async (invoiceId) => {
    setGeneratingPayLink(invoiceId);
    try {
      const res = await axios.post(`${API}/paypal/create-invoice-payment/${invoiceId}`);
      const payLink = `${window.location.origin}/pay/${res.data.order_id}`;
      setPaymentLink({ invoiceId, orderId: res.data.order_id, link: payLink, amount: res.data.amount });
      toast.success("Payment link generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate payment link");
    } finally {
      setGeneratingPayLink(null);
    }
  };

  const copyPaymentLink = () => {
    if (paymentLink) {
      navigator.clipboard.writeText(paymentLink.link);
      toast.success("Payment link copied to clipboard!");
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <header className="bg-[#0A3F2F] text-white py-4 px-4 md:px-8 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-to-admin">
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Admin</span>
            </Link>
            <div className="h-6 w-px bg-white/20 hidden sm:block" />
            <h1 className="font-['Syne'] text-lg md:text-xl font-bold flex items-center gap-2">
              <Receipt size={20} />
              Invoices
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchInvoices} className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors" data-testid="refresh-invoices">
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-[#D4FF33] text-[#0A3F2F] px-4 py-2 rounded-lg font-medium hover:bg-[#c4ef23] transition-colors"
              data-testid="create-invoice-btn"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">New Invoice</span>
            </button>
            <button 
              onClick={handleLogout}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-[#0A3F2F]">{formatCurrency(stats.total_revenue)}</p>
                  <p className="text-xs md:text-sm text-[#4A5F55]">Revenue</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Clock size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-[#0A3F2F]">{formatCurrency(stats.pending_amount)}</p>
                  <p className="text-xs md:text-sm text-[#4A5F55]">Pending</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-[#0A3F2F]">{formatCurrency(stats.overdue_amount)}</p>
                  <p className="text-xs md:text-sm text-[#4A5F55]">Overdue</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#D4FF33] flex items-center justify-center">
                  <FileText size={20} className="text-[#0A3F2F]" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-[#0A3F2F]">{stats.total_invoices}</p>
                  <p className="text-xs md:text-sm text-[#4A5F55]">Total</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter and Search */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5F55]" />
            <input
              type="text"
              placeholder="Search by client or invoice #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-white border border-[#D1D1C7] text-[#0A3F2F] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
              data-testid="search-invoices"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 rounded-lg bg-white border border-[#D1D1C7] text-[#0A3F2F] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
            data-testid="filter-status"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            onClick={checkOverdue}
            className="px-4 py-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors text-sm font-medium whitespace-nowrap"
            data-testid="check-overdue-btn"
          >
            Check Overdue
          </button>
        </div>

        {/* Invoices List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 md:p-6 border-b border-[#E0E0D8]">
            <h2 className="font-['Syne'] text-xl font-bold text-[#0A3F2F]">Invoice List</h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw size={32} className="animate-spin text-[#0A3F2F] mx-auto mb-4" />
              <p className="text-[#4A5F55]">Loading invoices...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-8 text-center" data-testid="no-invoices">
              <div className="w-16 h-16 rounded-full bg-[#EBEBE3] flex items-center justify-center mx-auto mb-4">
                <Receipt size={32} className="text-[#4A5F55]" />
              </div>
              <h3 className="font-['Syne'] text-lg font-bold text-[#0A3F2F] mb-2">No invoices yet</h3>
              <p className="text-[#4A5F55] mb-4">Create your first invoice to get started.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-[#0A3F2F] text-white px-6 py-2 rounded-full font-medium"
              >
                Create Invoice
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[#E0E0D8]">
              {invoices.map((invoice, index) => {
                const StatusIcon = statusIcons[invoice.status] || FileText;
                return (
                  <div key={invoice.id} className="p-4 md:p-6 hover:bg-[#F5F5F0] transition-colors" data-testid={`invoice-${index}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#0A3F2F] flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold">{invoice.client_name.charAt(0)}</span>
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-mono text-sm text-[#4A5F55]">{invoice.invoice_number}</span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[invoice.status]}`}>
                              <StatusIcon size={12} />
                              {invoice.status}
                            </span>
                          </div>
                          <h3 className="font-semibold text-[#0A3F2F]">{invoice.client_name}</h3>
                          <p className="text-sm text-[#4A5F55]">{invoice.client_email}</p>
                          {invoice.service && <p className="text-xs text-[#4A5F55] mt-1">{invoice.service}</p>}
                        </div>
                      </div>
                      <div className="flex flex-col md:items-end gap-2">
                        <p className="text-xl font-bold text-[#0A3F2F]">{formatCurrency(invoice.total)}</p>
                        <p className="text-xs text-[#4A5F55]">Due: {formatDate(invoice.due_date)}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {/* PDF Download */}
                          <button
                            onClick={() => downloadPdf(invoice.id)}
                            className="text-xs bg-[#EBEBE3] text-[#0A3F2F] px-3 py-1 rounded-full hover:bg-[#D1D1C7] transition-colors flex items-center gap-1"
                            data-testid={`download-pdf-${index}`}
                          >
                            <Download size={12} />
                            PDF
                          </button>
                          {/* Send Email */}
                          {invoice.status !== "paid" && invoice.status !== "cancelled" && (
                            <button
                              onClick={() => sendEmail(invoice.id)}
                              disabled={sendingEmail === invoice.id}
                              className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full hover:bg-blue-200 transition-colors flex items-center gap-1 disabled:opacity-50"
                              data-testid={`send-email-${index}`}
                            >
                              <Mail size={12} />
                              {sendingEmail === invoice.id ? "Sending..." : "Email"}
                            </button>
                          )}
                          {/* PayPal Payment Link */}
                          {invoice.status !== "paid" && invoice.status !== "cancelled" && (
                            <button
                              onClick={() => generatePaymentLink(invoice.id)}
                              disabled={generatingPayLink === invoice.id}
                              className="text-xs bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full hover:bg-yellow-200 transition-colors flex items-center gap-1 disabled:opacity-50"
                              data-testid={`paypal-link-${index}`}
                            >
                              <CreditCard size={12} />
                              {generatingPayLink === invoice.id ? "..." : "PayPal"}
                            </button>
                          )}
                          {/* Mark Paid */}
                          {invoice.status !== "paid" && invoice.status !== "cancelled" && (
                            <button
                              onClick={() => markAsPaid(invoice.id)}
                              className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full hover:bg-green-200 transition-colors"
                              data-testid={`mark-paid-${index}`}
                            >
                              Mark Paid
                            </button>
                          )}
                          <select
                            value={invoice.status}
                            onChange={(e) => updateStatus(invoice.id, e.target.value)}
                            className="text-xs px-2 py-1 rounded border border-[#D1D1C7] bg-white"
                            data-testid={`status-select-${index}`}
                          >
                            <option value="draft">Draft</option>
                            <option value="sent">Sent</option>
                            <option value="paid">Paid</option>
                            <option value="overdue">Overdue</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          <button
                            onClick={() => deleteInvoice(invoice.id)}
                            className="text-red-500 hover:text-red-700 p-1"
                            data-testid={`delete-invoice-${index}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <CreateInvoiceModal 
          onClose={() => setShowCreateModal(false)} 
          onSuccess={() => { setShowCreateModal(false); fetchInvoices(); }}
        />
      )}

      {/* Payment Link Modal */}
      {paymentLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <motion.div
            className="bg-white rounded-2xl w-full max-w-md p-6"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Syne'] text-xl font-bold text-[#0A3F2F] flex items-center gap-2">
                <CreditCard size={20} />
                PayPal Payment Link
              </h3>
              <button onClick={() => setPaymentLink(null)} className="p-2 hover:bg-[#F5F5F0] rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="bg-[#F5F5F0] rounded-xl p-4 mb-4">
              <p className="text-sm text-[#4A5F55] mb-2">Amount Due</p>
              <p className="text-2xl font-bold text-[#0A3F2F]">${paymentLink.amount?.toFixed(2)}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-[#0A3F2F] mb-2">Payment Link</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={paymentLink.link}
                  className="flex-1 px-3 py-2 rounded-lg border border-[#D1D1C7] bg-[#F5F5F0] text-sm text-[#4A5F55]"
                />
                <button
                  onClick={copyPaymentLink}
                  className="p-2 bg-[#0A3F2F] text-white rounded-lg hover:bg-[#0d5139]"
                  data-testid="copy-payment-link"
                >
                  <Copy size={18} />
                </button>
              </div>
            </div>

            <p className="text-xs text-[#4A5F55] text-center mb-4">
              Share this link with your client to accept PayPal payments
            </p>

            <button
              onClick={() => setPaymentLink(null)}
              className="w-full bg-[#0A3F2F] text-white py-3 rounded-xl font-medium hover:bg-[#0d5139]"
            >
              Done
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// Create Invoice Modal Component
function CreateInvoiceModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_address: "",
    service: "",
    notes: "",
    due_days: 30
  });
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [submitting, setSubmitting] = useState(false);

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === "quantity" || field === "unit_price" ? parseFloat(value) || 0 : value;
    setItems(newItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/invoices`, {
        ...formData,
        items: items.filter(item => item.description && item.unit_price > 0)
      });
      toast.success("Invoice created successfully");
      onSuccess();
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error("Failed to create invoice");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <motion.div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="sticky top-0 bg-white border-b border-[#E0E0D8] p-4 md:p-6 flex items-center justify-between">
          <h2 className="font-['Syne'] text-xl font-bold text-[#0A3F2F]">Create Invoice</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F5F5F0] rounded-lg" data-testid="close-modal">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0A3F2F] mb-1">Client Name *</label>
              <input
                type="text"
                required
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                className="w-full px-4 py-2 rounded-lg border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                data-testid="invoice-client-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0A3F2F] mb-1">Client Email *</label>
              <input
                type="email"
                required
                value={formData.client_email}
                onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                className="w-full px-4 py-2 rounded-lg border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                data-testid="invoice-client-email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0A3F2F] mb-1">Client Address</label>
            <input
              type="text"
              value={formData.client_address}
              onChange={(e) => setFormData({...formData, client_address: e.target.value})}
              className="w-full px-4 py-2 rounded-lg border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0A3F2F] mb-1">Service</label>
              <select
                value={formData.service}
                onChange={(e) => setFormData({...formData, service: e.target.value})}
                className="w-full px-4 py-2 rounded-lg border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
              >
                <option value="">Select service</option>
                {serviceOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0A3F2F] mb-1">Due in (days)</label>
              <input
                type="number"
                min="1"
                value={formData.due_days}
                onChange={(e) => setFormData({...formData, due_days: parseInt(e.target.value) || 30})}
                className="w-full px-4 py-2 rounded-lg border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
              />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <label className="block text-sm font-medium text-[#0A3F2F] mb-2">Line Items *</label>
            <div className="space-y-2">
              {items.map((item, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(index, "description", e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-[#D1D1C7] text-sm focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                    data-testid={`item-desc-${index}`}
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, "quantity", e.target.value)}
                    className="w-16 px-2 py-2 rounded-lg border border-[#D1D1C7] text-sm focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    min="0"
                    step="0.01"
                    value={item.unit_price || ""}
                    onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                    className="w-24 px-2 py-2 rounded-lg border border-[#D1D1C7] text-sm focus:outline-none focus:ring-2 focus:ring-[#0A3F2F]"
                    data-testid={`item-price-${index}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addItem}
              className="mt-2 text-sm text-[#0A3F2F] font-medium flex items-center gap-1 hover:underline"
            >
              <Plus size={14} /> Add Item
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0A3F2F] mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={2}
              className="w-full px-4 py-2 rounded-lg border border-[#D1D1C7] focus:outline-none focus:ring-2 focus:ring-[#0A3F2F] resize-none"
            />
          </div>

          {/* Total */}
          <div className="bg-[#F5F5F0] rounded-xl p-4 flex justify-between items-center">
            <span className="font-medium text-[#0A3F2F]">Total:</span>
            <span className="text-2xl font-bold text-[#0A3F2F]">
              ${calculateTotal().toFixed(2)}
            </span>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-[#D1D1C7] rounded-full font-medium text-[#0A3F2F] hover:bg-[#F5F5F0] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || items.every(i => !i.description || i.unit_price <= 0)}
              className="flex-1 px-4 py-3 bg-[#0A3F2F] text-white rounded-full font-medium hover:bg-[#0d5139] transition-colors disabled:opacity-50"
              data-testid="submit-invoice"
            >
              {submitting ? "Creating..." : "Create Invoice"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
